<script>
  import { onMount } from "svelte";
  import { csv } from "d3-fetch";
  import { calculator as copy } from "./../content/data.json";
  import stateAbbreviationsToNames from "./../data/stateAbbreviationsToNames.json";

  import Number from "./Number.svelte";
  import Toggle from "./Toggle.svelte";
  import Text from "./Text.svelte";
  import Minimap from "./Minimap.svelte";
  import CeIcon from "./CeIcon.svelte";
  import NzIcon from "./NzIcon.svelte";
  import ShareImage from "./ShareImage.svelte";
  import SearchableInput from "./SearchableInput.svelte";
  import CalculatorTooltip from "./CalculatorTooltip.svelte";
  import { fromPairs, getNumberWithUnits } from "./../utils";

  // import data from "./../data/sample_data.csv";

  let data = [];
  let regions = [];
  let otherStatsData = [];
  let selectedRegion = {};
  let statsByName = {};

  const fileNames = {
    rcp45:
      "/assets/lives_saved_adapt_costs_rcp45_2050_final_outputs_popplaces_Nov132020_consumption_clip_noland_discount_rcp45_nz_lives_2050.csv",
    rcp85:
      "/assets/lives_saved_adapt_costs_rcp85_2050_final_outputs_popplaces_Nov132020_consumption_clip_noland_discount_rcp85_nz_lives_2050.csv",
    core:
      "/assets/lives_saved_adapt_costs_ssp370_2050_final_outputs_popplaces_Nov132020_consumption_clip_noland_discount_ssp370_nz_lives_2050.csv"
  };
  const otherStatsInfo = [
    {
      fileName: fileNames["rcp45"],
      label: "Moderate emissions scenario:",
      inlineLabel: "rcp45"
    },
    {
      fileName: fileNames["rcp85"],
      label: "High emissions scenario:",
      inlineLabel: "rcp85"
    }
  ];
  const loadData = async () => {
    const res = await csv(fileNames["core"]);
    const dataWithId = res.map(d => ({
      ...d,
      id: d["NAME"]
    }));
    statsByName = {};
    dataWithId.forEach(d => {
      statsByName[d.id] = d;
    });

    regions = dataWithId.map(d => d.id);
    selectedRegion = "Boston, MA";

    otherStatsData = [];
    for (const stat of otherStatsInfo) {
      const otherRes = await csv(stat["fileName"]);
      const otherDataWithId = otherRes.map(d => ({
        ...d,
        id: d["NAME"]
      }));
      let otherStatsByName = {};
      otherDataWithId.forEach(d => {
        otherStatsByName[d.id] = d;
      });
      otherStatsData = [...otherStatsData, otherStatsByName];
    }
  };
  onMount(loadData);

  const targetOptions = [
    {
      id: "CE",
      label: copy["policy_1_label"],
      description: copy["policy_1_description"],
      icon: CeIcon
    },
    {
      id: "NZ",
      label: copy["policy_2_label"],
      description: copy["policy_2_description"],
      icon: NzIcon
    }
  ];
  let selectedTarget = targetOptions[0].id;
  $: selectedTargetLabel = targetOptions
    .find(d => d.id == selectedTarget)
    .label.toLowerCase();
  $: selectedTargetDescription =
    copy[
      selectedTarget == "CE" ? "policy_1_description" : "policy_2_description"
    ];

  $: usStats = statsByName["United States"];
  $: selectedRegionStats = statsByName[selectedRegion] || {};

  $: stateStats =
    statsByName[
      stateAbbreviationsToNames[(selectedRegionStats || {})["STATE_ABBREV"]]
    ] || {};
  $: selectedRegionLocation = [
    +selectedRegionStats["LON"],
    +selectedRegionStats["LAT"]
  ];

  const stateNameMasks = {
    "District Columbia": "the District of Columbia"
  };
  $: sections = [
    selectedRegionStats && {
      name: selectedRegion,
      lives: selectedRegionStats[`${selectedTarget}_LIVES`],
      cost: selectedRegionStats[`${selectedTarget}_COST`]
    },
    stateStats && {
      name: stateNameMasks[stateStats["NAME"]] || stateStats["NAME"],
      lives: stateStats[`${selectedTarget}_LIVES`],
      cost: stateStats[`${selectedTarget}_COST`]
    },
    usStats && {
      name: "the United States",
      lives: usStats[`${selectedTarget}_LIVES`],
      cost: usStats[`${selectedTarget}_COST`]
    }
  ].filter(d => d);

  $: otherStats = [
    selectedRegion,
    stateAbbreviationsToNames[(selectedRegionStats || {})["STATE_ABBREV"]],
    "United States"
  ].map(region =>
    fromPairs(
      ["lives", "cost"].map(type => [
        type,
        otherStatsInfo.map((stat, i) => ({
          ...stat,
          i,
          otherStatsData: otherStatsData.length,
          oi: otherStatsData[i],
          value: ((otherStatsData[i] || {})[region] || {})[
            `${selectedTarget}_${type.toUpperCase()}`
          ]
        }))
      ])
    )
  );
</script>

<figure class="c">
  <div class="left">
    <div class="search">
      <SearchableInput
        bind:value="{selectedRegion}"
        placeholder="Search for a city, county, or state"
        options="{regions}"
        emptyNote="No regions found" />
    </div>

    <div class="minimap">
      <Minimap location="{selectedRegionLocation}" />
    </div>

    {#if selectedRegionStats}
      <h6>
        {@html copy.toggle_text}
      </h6>
      <!-- <Toggle options="{targetOptions}" bind:value="{selectedTarget}" /> -->
      <div class="options">
        {#each targetOptions as { id, label, description, icon }}
          <button
            class="option"
            class:selected="{id == selectedTarget}"
            on:click="{() => (selectedTarget = id)}">
            <div class="icon">
              <svelte:component this="{icon}" />
            </div>
            <div class="option-title">{label}</div>
            <div class="description">
              <Text text="{description}" />
            </div>
          </button>
        {/each}
      </div>
    {/if}
  </div>

  <div class="right">
    {#each sections as { name, label, lives, cost }, i}
      <div class="section">
        <div class="share-button">
          <ShareImage
            {...{ name, label, lives, cost, selectedTarget, selectedTargetLabel }} />
        </div>
        If
        <strong>{name}</strong>
        achieves
        <span class="{`highlight highlight-${selectedTarget}`}">
          {selectedTargetLabel}
        </span>
        by 2050, we would save
        <CalculatorTooltip
          stats="{(otherStats[i] || {})['lives']}"
          format="{getNumberWithUnits}"
          suffix="{Math.round((otherStats[i] || {})['lives']) == 1 ? 'life' : 'lives'}">
          <div class="stat">
            <Number number="{lives}" format="{getNumberWithUnits}" />
            {Math.round(lives) == 1 ? 'life' : 'lives'}
          </div>
        </CalculatorTooltip>
        and avoid
        <CalculatorTooltip
          stats="{(otherStats[i] || {})['cost']}"
          format="{d => `$${getNumberWithUnits(d)}`}"
          suffix="{Math.round((otherStats[i] || {})['cost']) == 1 ? 'dollar' : 'dollars'}">
          <div class="stat">
            <Number
              number="{cost}"
              format="{d => `$${getNumberWithUnits(d)}`}" />
          </div>
        </CalculatorTooltip>
        in adaptation costs globally through 2100.
      </div>
    {/each}
  </div>
</figure>

<style>
  .c {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -1em 0 -1em;
  }
  .left {
    flex: 1;
    min-width: min(22em, 90vw);
    max-width: 100%;
    padding: 1em;
    margin-left: -1em;
  }
  .minimap {
    margin-bottom: 2em;
  }
  .right {
    position: sticky;
    top: 3em;
    flex: 2;
    min-width: min(22em, 90vw);
    max-width: 100%;
    padding: 0.6em 2.3em 0.6em 2.9em;
    box-shadow: var(--box-shadow);
    /* border: 1px solid var(--c3); */
    background: #f5f5f9;
    align-self: flex-start;
    margin-left: 3em;
  }
  h6 {
    margin-bottom: 0.5em;
  }
  .search {
    position: relative;
    margin-bottom: 2em;
  }
  .item {
    margin: 1em 0;
  }
  strong {
    font-weight: 800;
  }
  .highlight {
    background-image: linear-gradient(120deg, #d4f1e0, #ebd3f4, #f9cdcd);
    background-repeat: no-repeat;
    background-size: 100% 0.3em;
    background-position: 0px 89%;
    transition: all 0.3s ease-out;
    /* font-style: italic;
    font-weight: 600; */
  }
  .highlight-CE {
    background-image: linear-gradient(120deg, #cdf0fb, #fbefca);
  }
  .top {
    /* font-size: 0.86em; */
    margin-bottom: 0.3em;
  }
  .stats {
    /* display: flex; */
    margin: 1em 0 0;
  }
  .stat {
    display: inline;
    /* position: relative; */
    /* padding: 0 0.2em; */
    /* background: white; */
  }
  .note {
    /* text-align: right; */
    padding-bottom: 0.6em;
  }
  .stat :global(span) {
    font-weight: 800;
    /* font-size: 1.3em; */
    color: var(--c2);
  }
  .section {
    position: relative;
    padding: 2em 0;
    line-height: 1.8em;
  }
  .section + .section {
    border-top: 1px solid white;
  }
  .share-button {
    position: absolute;
    /* bottom: 1em; */
    left: -1.7em;
  }
  .options {
    margin: 0.5em -2.1em;
  }
  .option {
    appearance: none;
    background: none;
    outline: none;
    text-align: left;
    margin: 0.6em;
    padding: 0.8em 1.6em;
    border: none;
    font-family: "Inter", sans-serif;
    font-size: 1em;
    border-radius: 1.3em;
    border: 2px solid transparent;
    cursor: pointer;
  }
  .option:hover,
  .option:focus {
    /* border-color: var(--c2); */
    background: #bec7c711;
    border-color: #bec7c722;
    /* box-shadow: var(--box-shadow); */
  }
  .option:active,
  .option.selected {
    /* border-color: var(--c2); */
    background: #45aeb111;
    border-color: #45aeb122;
    /* box-shadow: var(--box-shadow); */
  }
  .icon {
    width: 15em;
    font-size: 0.6em;
    margin-bottom: 0.8em;
  }
  .icon :global(svg) {
    width: 100%;
    height: 10em;
  }
  .option-title {
    font-weight: 800;
  }
  .description {
    font-size: 0.85em;
    line-height: 1.55em;
  }
  @media (max-width: 800px) {
    .right {
      margin-left: 0;
    }
    .left {
      margin-left: 0;
    }
    .option {
      margin-bottom: 0;
    }
  }
</style>
